/**
 * DrapeauDeLangue — la langue d'un message ou d'un fan, en drapeau : sous une bulle traduite (la
 * langue dans laquelle le fan a écrit), à côté du nom dans l'en-tête et dans la liste. Dessiné en
 * SVG par le `Drapeau` de la famille ventes (Windows n'a pas les drapeaux emoji, et une image
 * distante se fait bloquer). Kayla : « trad + drapeau », toujours.
 *
 * Les trente-quatre langues que Jev lit (la table du rail des conversations, `COUNTRY_BY_LANG`)
 * ont toutes leur drapeau dessiné (vague 2, 02-11-2026 : grec, hébreu, ukrainien, roumain,
 * tchèque… tombaient en lettres). Une langue hors table s'écrit en pastille (« XX ») : le
 * chatteur voit toujours qu'il y a une langue.
 *
 *   <DrapeauDeLangue langue="en" />            → 🇬🇧
 *   <DrapeauDeLangue langue="pt" pays="BR" />  → le Brésil, quand on sait d'où il écrit
 */
import { Drapeau, PAYS_DESSINES } from "../ventes/Drapeau";
import { cn } from "../utils";
import "./conversation.css";

/** La langue → le pays de son drapeau (ISO 639-1 → ISO 3166-1). La table de Jev, en entier. */
export const PAYS_DE_LA_LANGUE: Readonly<Record<string, string>> = {
  en: "GB",
  fr: "FR",
  es: "ES",
  de: "DE",
  it: "IT",
  pt: "PT",
  nl: "NL",
  pl: "PL",
  ru: "RU",
  ja: "JP",
  ko: "KR",
  zh: "CN",
  ar: "SA",
  tr: "TR",
  sv: "SE",
  fi: "FI",
  el: "GR",
  he: "IL",
  uk: "UA",
  ro: "RO",
  cs: "CZ",
  hu: "HU",
  da: "DK",
  no: "NO",
  bg: "BG",
  hr: "HR",
  sk: "SK",
  hi: "IN",
  id: "ID",
  th: "TH",
  vi: "VN",
  fa: "IR",
  ms: "MY",
  tl: "PH",
};

export interface DrapeauDeLangueProps {
  /** Code ISO 639-1 (« en », « FR », « pt-BR »). Absent : rien. */
  langue: string | null | undefined;
  /** Le pays, quand on le sait mieux que la langue (un fan brésilien écrit en « pt »). */
  pays?: string | null;
  /** Largeur en px (le drapeau fait 4:3). Défaut 18. */
  taille?: number;
  className?: string;
}

export function DrapeauDeLangue({ langue, pays, taille = 18, className }: DrapeauDeLangueProps) {
  if (!langue) return null;
  const code = langue.toLowerCase().split(/[-_]/)[0] ?? "";
  if (!code) return null;
  const codePays = (pays ?? PAYS_DE_LA_LANGUE[code] ?? "").toUpperCase();
  if (codePays && PAYS_DESSINES.has(codePays)) {
    return (
      <span className={cn("inline-flex", className)} role="img" aria-label={code.toUpperCase()} title={code.toUpperCase()}>
        <Drapeau pays={codePays} taille={taille} className="cv-drapeau ring-0" />
      </span>
    );
  }
  return (
    <span
      className={cn("cv-langue", className)}
      style={{ minWidth: taille, height: Math.round(taille * 0.75) }}
      title={code.toUpperCase()}
    >
      {code.toUpperCase()}
    </span>
  );
}
